import * as Yup from 'yup';
import { parse, isAfter, fromUnixTime } from 'date-fns';
import { REQUEST_VALIDATION_LIMITS } from './v1_adapter';

// =============================================================================
// VALIDATION ARCHITECTURE OVERVIEW
// =============================================================================
//
// This schema implements a TWO-TIER validation system for the new request form.
// Each tier has a distinct purpose and runs at different points in the user flow.
//
// ┌─────────────────────────────────────────────────────────────────────────┐
// │ TIER 1: EDITING CONSTRAINTS (Input Gatekeeper)                          │
// │ Purpose:  Prevent invalid data from entering state                      │
// │ When:     Every keystroke/change (via computeStateUpdate)               │
// │ Enforces: MAX lengths, enum values, data types                          │
// │ Schema:   editingConstraintsSchema                                       │
// │ Function: validationErrors(state) → { field: "error message" }          │
// │                                                                          │
// │ Example: User types 151 chars → state rejected, old value kept          │
// └─────────────────────────────────────────────────────────────────────────┘
//
// ┌─────────────────────────────────────────────────────────────────────────┐
// │ TIER 2: PERSISTENCE VALIDATION (Completeness Requirements)              │
// │ Purpose:  Enforce strict requirements for saving/posting                │
// │ When:     Every keystroke (same as TIER 1) - errors always computed    │
// │ Enforces: MIN lengths, required field combinations                      │
// │ Schemas:  draftMinimumSchema (draft save)                               │
// │           postMediaRequiredSchema, postLogisticsRequiredSchema,         │
// │           postDeadlineRequiredSchema (full post)                        │
// │ Function: validationErrors(state, level) → { field: "error message" }  │
// │ Display:  Controlled by computeFieldHints() based on validationActive  │
// │           or touchedFields (blur-based validation)                      │
// │                                                                          │
// │ Example: Draft needs title+details min, Post needs media+deadline too  │
// └─────────────────────────────────────────────────────────────────────────┘
//
// KEY PRINCIPLES:
//
// 1. ALL VALIDATIONS ALWAYS RUN
//    - Both TIER 1 and TIER 2 validations run on every keystroke
//    - All errors are computed and stored in state.errors
//    - Display is controlled separately by computeFieldHints()
//
// 2. DISPLAY IS FILTERED BASED ON USER INTERACTION
//    - Errors show when: validationActive=true OR touchedFields[field]=true
//    - validationActive becomes true on save/publish attempt
//    - touchedFields tracks when text fields are blurred
//    - Result: progressive disclosure (blur shows errors early, submit shows all)
//
// 3. PERSISTENCE GATES STILL EXIST
//    - Draft: Minimal viable data (title + description minimums) - level="draft"
//    - Post: Complete, valid data (media outlet, deadline, logistics) - level="full"
//    - Save operations re-validate at appropriate level to block incomplete requests
//
// 4. DERIVED STATE FOR UI DISPLAY
//    - computeFieldHints() produces display strings and CSS classes
//    - Display priority: error message > hint > character count
//    - Components consume pre-computed values, no validation logic in UI
//
// HOW TO ADD NEW VALIDATION:
//
// - Max length constraint?     → Add .max() to field in editingConstraintsSchema
// - Min length for draft?      → Add to draftMinimumSchema
// - Required for posting?      → Add to appropriate post*RequiredSchema
// - Display hint needed?        → Add to computeFieldHints()
//
// DATA FLOW:
//
//   User types → computeStateUpdate() → validationErrors(state, "full")
//                                     ↓
//                               Runs TIER 1 + TIER 2 (full level)
//                               Stores ALL errors in state.errors
//                                     ↓
//                         populateDerivedState() → computeFieldHints()
//                                     ↓
//                         Filter errors based on validationActive || touchedFields[field]
//                                     ↓
//                         UI displays filtered errors
//
//   User blurs field → markFieldTouched(fieldName)
//                   → touchedFields[fieldName] = true
//                   → computeFieldHints() shows errors for that field
//
//   User clicks save/publish → validationErrors(state, level)
//                            → Re-validate at appropriate level
//                            → Block or allow based on errors
//                            → Set validationActive=true (shows all errors)
//
// =============================================================================

// Extract validation limits from v1_adapter (single source of truth)
// Matches backend constants
export const VALIDATION_LIMITS = REQUEST_VALIDATION_LIMITS;

const DEADLINE_OPTIONS = ['24hrs', '72hrs', '7days', 'custom'];
const DISTRIBUTIONS = ['online', 'radio', 'podcast', 'video', 'event', 'other'];
const PERSON_TYPES = ['expert', 'man_on_the_street'];
const EVENT_TYPES = ['conference', 'lecture', 'webinar'];
const LOOKING_FOR_OPTIONS = ['person', 'product'];
export const LOGISTICS_FIELD_NAMES = [
  'logisticsInPerson',
  'logisticsLive',
  'logisticsPreRecorded',
  'logisticsRemote',
  'logisticsVideo',
];
const NOTIFICATION_OPTIONS = ['every-pitch', 'digest'];
const SAVING_STATES = ['idle', 'pending', 'saving'];
const SCHEDULE_OPTIONS = ['now', 'custom'];
const UI_STATES = ['loading', 'loading-failed', 'form-revealed', 'reviewing', 'posted'];

// Helper for proper pluralization
export function pluralize(count, singular, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

// Compute hint for fields that haven't met minimum length requirements
// Returns null if minimum is met, otherwise returns hint string
export function computeMinLengthHint(value, minLength) {
  const currentLength = (value || '').length;

  if (currentLength === 0) {
    return `... needs at least ${minLength} ${pluralize(minLength, 'character')}`;
  }

  if (currentLength < minLength) {
    const needed = minLength - currentLength;
    return `... needs ${needed} more ${pluralize(needed, 'character')}`;
  }

  return null; // Meets minimum - no hint needed
}

// Helper to create smart min length error messages for Yup validation
// Returns short format suitable for display next to field labels
export function createMinLengthMessage(fieldName, minLength) {
  return function validateMinLength({ value }) {
    return computeMinLengthHint(value, minLength);
  };
}

const VALIDATION_MESSAGES = {
  CUSTOM_DEADLINE_REQUIRED: 'Custom deadline date time must be specified and after the post time if opted in',
  CUSTOM_SCHEDULE_REQUIRED: 'Custom schedule date time must be specified and in the future when opted in',
  DEADLINE_INVALID: 'Invalid deadline option',
  DEADLINE_REQUIRED: 'Deadline is required',
  LOGISTICS_REQUIRED: 'Please select at least one logistics option',
  LOOKING_FOR_REQUIRED: 'Looking for must be either "person" or "product"',
  MEDIA_OPTION_ID_REQUIRED: 'Media option ID is required',
  MEDIA_OPTION_TITLE_REQUIRED: 'Media option title is required',
  MEDIA_OUTLET_REQUIRED: 'Media outlet is required',
  SCHEDULE_INVALID: 'Invalid schedule option',
  SCHEDULE_REQUIRED: 'Schedule is required',
  SPEAKER_EVENT_TYPE_REQUIRED: 'An event type must be selected for speaker requests',
};

export function resolveRequestScheduledTime({ customScheduleDateTime, schedule, timeNow }) {
  if (schedule === 'custom' && customScheduleDateTime) {
    return parse(customScheduleDateTime, "yyyy-MM-dd'T'HH:mm", new Date());
  }
  return fromUnixTime(timeNow);
}

// Helper function to check if at least one logistics option is selected when
// logistics section is visible (distribution is radio/podcast/video/event)
// This is used for EACH logistics field to determine if validation should pass
function requiresLogisticsSelection(value, context) {
  const { distribution } = context.parent;

  // Only validate logistics when section is visible
  const logisticsRequired = ['radio', 'podcast', 'video', 'event'].includes(distribution);

  if (!logisticsRequired) {
    return true; // Validation passes when logistics section not shown
  }

  // At least one logistics option must be selected
  return LOGISTICS_FIELD_NAMES.some((fieldName) => context.parent[fieldName]);
}

// Custom validation function for deadline datetime
function validateCustomDeadline(value, context) {
  const {
 customScheduleDateTime, deadline, schedule, timeNow,
} = context.parent;

  if (deadline !== 'custom') return true;
  if (!value) return false;

  const scheduledTime = resolveRequestScheduledTime({ customScheduleDateTime, schedule, timeNow });
  return isAfter(parse(value, "yyyy-MM-dd'T'HH:mm", new Date()), scheduledTime);
}

// Custom validation function for schedule datetime
function validateCustomSchedule(value, context) {
  const { schedule, timeNow } = context.parent;

  if (schedule !== 'custom') return true;
  if (!value) return false;

  return isAfter(parse(value, "yyyy-MM-dd'T'HH:mm", new Date()), fromUnixTime(timeNow));
}

const lookingForField = Yup.string()
  .default(LOOKING_FOR_OPTIONS[0])
  .oneOf(LOOKING_FOR_OPTIONS, VALIDATION_MESSAGES.LOOKING_FOR_REQUIRED);
const requestSummaryField = Yup.string().default('').max(VALIDATION_LIMITS.title.max, `Title length exceeds ${VALIDATION_LIMITS.title.max} characters`);
const targetMediaField = Yup.string().nullable().default('');
const targetMediaFieldRequired = Yup.string()
  .required(VALIDATION_MESSAGES.MEDIA_OUTLET_REQUIRED)
  .min(1, VALIDATION_MESSAGES.MEDIA_OUTLET_REQUIRED);
const eventTypeField = Yup.string()
  .default(EVENT_TYPES[0])
  .nullable()
  .oneOf([...EVENT_TYPES, null]);

const personTypeField = Yup.string().default(PERSON_TYPES[0]).oneOf(PERSON_TYPES);

const detailedDescriptionField = Yup.string().default('').max(VALIDATION_LIMITS.details.max, `Description cannot exceed ${VALIDATION_LIMITS.details.max} characters`);
const detailedDescriptionFieldRequired = Yup.string()
  .required()
  .max(VALIDATION_LIMITS.details.max, `Description cannot exceed ${VALIDATION_LIMITS.details.max} characters`)
  .min(VALIDATION_LIMITS.details.min, createMinLengthMessage('Description', VALIDATION_LIMITS.details.min));

const draftMinimumSchema = Yup.object().shape({
  title: requestSummaryField
    .required()
    .min(VALIDATION_LIMITS.title.min, createMinLengthMessage('Request summary', VALIDATION_LIMITS.title.min)),
  details: detailedDescriptionFieldRequired,
});

// Event type validation: required when distribution is 'event' (speaker auto-designation)
const eventTypeRequiredForSpeaker = function validateEventType(value, context) {
  const { speakerRequest } = context.parent;
  if (!speakerRequest) {
    return true;
  }
  return !!value;
};

const postMediaRequiredSchema = Yup.object().shape({
  eventType: eventTypeField.test(
    'event-type-required',
    VALIDATION_MESSAGES.SPEAKER_EVENT_TYPE_REQUIRED,
    eventTypeRequiredForSpeaker,
  ),
  mediaOutletId: targetMediaFieldRequired,
});

const postLogisticsRequiredSchema = Yup.object().shape(
  LOGISTICS_FIELD_NAMES.reduce(
    (shape, fieldName) => ({
      ...shape,
      [fieldName]: Yup.boolean().test(
        'logistics-required',
        VALIDATION_MESSAGES.LOGISTICS_REQUIRED,
        requiresLogisticsSelection,
      ),
    }),
    {},
  ),
);

const deadlineBaseSchema = Yup.object().shape({
  deadline: Yup.string()
    .required(VALIDATION_MESSAGES.DEADLINE_REQUIRED)
    .oneOf(DEADLINE_OPTIONS, VALIDATION_MESSAGES.DEADLINE_INVALID)
    .default('7days'),
  customDeadlineDateTime: Yup.string().nullable().default(''),
  schedule: Yup.string()
    .required(VALIDATION_MESSAGES.SCHEDULE_REQUIRED)
    .oneOf(SCHEDULE_OPTIONS, VALIDATION_MESSAGES.SCHEDULE_INVALID)
    .default('now'),
  customScheduleDateTime: Yup.string().nullable().default(''),
  notification: Yup.string().required().oneOf(NOTIFICATION_OPTIONS).default(NOTIFICATION_OPTIONS[0]),
});

const postDeadlineRequiredSchema = deadlineBaseSchema.concat(
  Yup.object().shape({
    customDeadlineDateTime: Yup.string().test(
      'custom-deadline-validation',
      VALIDATION_MESSAGES.CUSTOM_DEADLINE_REQUIRED,
      validateCustomDeadline,
    ),
    customScheduleDateTime: Yup.string().test(
      'custom-schedule-validation',
      VALIDATION_MESSAGES.CUSTOM_SCHEDULE_REQUIRED,
      validateCustomSchedule,
    ),
  }),
);

// =============================================================================
// TIER 1: EDITING CONSTRAINTS SCHEMA (Editing Tier)
// =============================================================================
// Runs on EVERY state change via computeStateUpdate() in useSyncedState.js
// Enforces: MAX lengths, enum values, data types
// Does NOT enforce: MIN lengths (those are in TIER 2 completion schemas)
// If validation fails: state change is REJECTED, old value kept, error added to state.errors
//

// Build logistics fields shape dynamically from the constant
const logisticsFieldsShape = LOGISTICS_FIELD_NAMES.reduce(
  (shape, fieldName) => ({
    ...shape,
    [fieldName]: Yup.boolean().default(false),
  }),
  {},
);

const editingConstraintsSchema = deadlineBaseSchema.concat(
  Yup.object().shape({
    // Request Type fields
    lookingFor: lookingForField.nullable(),

    // UI state
    errors: Yup.object().default({}),
    savingState: Yup.string().oneOf(SAVING_STATES).default(SAVING_STATES[0]),
    currentUiState: Yup.string().oneOf(UI_STATES).default('form-revealed'),

    // Request fields
    title: requestSummaryField,
    details: detailedDescriptionField,

    // Person subtype (expert vs man_on_the_street)
    personType: personTypeField,
    speakerRequest: Yup.boolean().default(false),

    // Media and distribution
    mediaOutletId: targetMediaField,
    distribution: Yup.string().default(DISTRIBUTIONS[0]).nullable().oneOf([...DISTRIBUTIONS, null]),
    eventType: eventTypeField,

    // Logistics (dynamically built from LOGISTICS_FIELD_NAMES)
    ...logisticsFieldsShape,
    equipmentNotes: Yup.string().nullable().default('').max(VALIDATION_LIMITS.equipment_notes.max, `Equipment notes cannot exceed ${VALIDATION_LIMITS.equipment_notes.max} characters`),
    logisticsUrl: Yup.string().nullable().default(''),

    // Opportunity flags
    distributeExternally: Yup.boolean().default(true), // Match V2 DB default (TRUE)
    paid: Yup.boolean().default(false),
    backlinkOpportunity: Yup.boolean().default(false),

    // System fields
    timeNow: Yup.number().required().default(0),
    lastSavedVersion: Yup.number().required().default(0),
    lastSavedAt: Yup.number().nullable().default(null),
    version: Yup.number().required().default(0),
    requestStatus: Yup.string().nullable().oneOf(['draft', 'scheduled', 'posted', null]).default(null),

    // Save state tracking
    savingError: Yup.string().nullable().oneOf([null, 'auto', 'manual']).default(null),

    // Persistence
    sourceRequestId: Yup.number().nullable().default(null),
    slug: Yup.string().nullable().default(null),

    // Validation control
    validationActive: Yup.boolean().default(false),

    // Blur-based validation tracking
    touchedFields: Yup.object().default({}),
  }),
);

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

// Helper to collect errors from a Yup schema
function collectYupErrors(schema, params) {
  try {
    schema.validateSync(params, { abortEarly: false });
    return {};
  } catch (err) {
    const errors = {};
    if (err.inner) {
      err.inner.forEach((error) => {
        if (error.path) {
          // Field-level error (e.g., "mediaOutletId", "title", "logisticsInPerson")
          errors[error.path] = error.message;
        } else if (error.type) {
          // Object-level test error without specific field path
          // (should be rare now that we use field-level validation)
          errors[error.type] = error.message;
        }
      });
    }
    return errors;
  }
}

// Helper to get unique error messages from an errors object
// Used to display deduplicated messages underneath groups of related fields
// Returns array of unique error messages
export function getUniqueErrorMessages(errors, fields) {
  if (!errors || !fields) return [];

  const messages = new Set();
  fields.forEach((field) => {
    if (errors[field]) {
      messages.add(errors[field]);
    }
  });

  return Array.from(messages);
}

export function validationErrors(params, level = 'full') {
  // Always validate editing constraints (TIER 1: max lengths, types)
  const errors = collectYupErrors(editingConstraintsSchema, params);

  // Always validate completion requirements (TIER 2)
  // Display filter in computeFieldHints() controls when errors are shown to user
  if (level === 'draft') {
    // Save & Exit: only validate title + description minimum
    Object.assign(errors, collectYupErrors(draftMinimumSchema, params));
  } else {
    // Review & Post: validate all requirements
    Object.assign(errors, collectYupErrors(draftMinimumSchema, params));
    Object.assign(errors, collectYupErrors(postMediaRequiredSchema, params));
    Object.assign(errors, collectYupErrors(postLogisticsRequiredSchema, params));
    Object.assign(errors, collectYupErrors(postDeadlineRequiredSchema, params));
  }

  return errors;
}

function lengthOutOfMax(text, max) {
  return `${(text || '').length}/${max}`;
}

export function computeFieldHints(state) {
  const anyLogisticsTouched = LOGISTICS_FIELD_NAMES.some((f) => state.touchedFields?.[f]);

  return {
    titleDisplay: lengthOutOfMax(state.title, VALIDATION_LIMITS.title.max),
    detailsDisplay: lengthOutOfMax(state.details, VALIDATION_LIMITS.details.max),
    equipmentNotesDisplay: lengthOutOfMax(state.equipmentNotes, VALIDATION_LIMITS.equipment_notes.max),

    // Pass through for components to access
    validationActive: state.validationActive,
    // Filter errors: show when validationActive OR field has been touched (blurred)
    validationErrors: state.validationActive
      ? Object.fromEntries(Object.entries(state.errors || {}).filter(([, v]) => Boolean(v)))
      : Object.fromEntries(
          Object.entries(state.errors || {}).filter(([field, msg]) => {
            if (!msg) return false;
            if (state.touchedFields?.[field]) return true;
            if (LOGISTICS_FIELD_NAMES.includes(field) && anyLogisticsTouched) return true;
            return false;
          }),
        ),
  };
}

// =============================================================================
// TIER 2: VALIDATION HELPERS
// =============================================================================

function isPostMinimumMet(state) {
  if (!state) return false;
  return draftMinimumSchema.isValidSync(state, { abortEarly: true });
}

// =============================================================================
// TIER 3: PERSISTENCE READINESS CHECKERS
// =============================================================================
// Gate functions that control when Save/Post buttons are enabled.
// Used for auto-save triggering and form validation.
//
// - isReadyForDraftSaving() → Minimal data for auto-save: title 3+ chars AND description 10+ chars
//

export function isReadyForDraftSaving(state) {
  return isPostMinimumMet(state);
}

//
// INITIAL STATE GENERATION
//
export function getInitialState() {
  return editingConstraintsSchema.getDefault();
}
